/**
 * Dashboard Layout Store - Manages layout state for the Dashboard workspace.
 *
 * Persists DataTree and inspector visibility, the sizes of the tree split and
 * the inspector split, and the maximized flag, so a user's chosen layout
 * survives reloads. Mirrors `queryConsoleLayoutStore`.
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"

interface DashboardLayoutStore {
  dataTreeVisible: boolean
  inspectorVisible: boolean
  treeSplitSizes: [number, number]
  inspectorSplitSizes: [number, number]
  isMaximized: boolean
  toggleDataTree: () => void
  toggleInspector: () => void
  setTreeSplitSizes: (sizes: number[]) => void
  setInspectorSplitSizes: (sizes: number[]) => void
  toggleMaximized: () => void
  setMaximized: (maximized: boolean) => void
  resetLayout: () => void
}

const DEFAULTS = {
  dataTreeVisible: true, inspectorVisible: true,
  treeSplitSizes: [22, 78] as [number, number], inspectorSplitSizes: [72, 28] as [number, number],
  isMaximized: false,
}
/** The first two sizes, or `fallback` when the splitter reports fewer than two panes. */
const toPair = (sizes: number[], fallback: [number, number]): [number, number] =>
  sizes.length >= 2 ? [sizes[0], sizes[1]] : fallback

export const useDashboardLayoutStore = create<DashboardLayoutStore>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      toggleDataTree: () => set((s) => ({ dataTreeVisible: !s.dataTreeVisible })),
      toggleInspector: () => set((s) => ({ inspectorVisible: !s.inspectorVisible })),
      setTreeSplitSizes: (sizes) => set({ treeSplitSizes: toPair(sizes, DEFAULTS.treeSplitSizes) }),
      setInspectorSplitSizes: (sizes) => set({ inspectorSplitSizes: toPair(sizes, DEFAULTS.inspectorSplitSizes) }),
      toggleMaximized: () => set((s) => ({ isMaximized: !s.isMaximized })),
      setMaximized: (isMaximized) => set({ isMaximized }),
      resetLayout: () => set({ ...DEFAULTS }),
    }),
    {
      name: "dataq-dashboard-layout",
      version: 1,
      partialize: (s) => ({
        dataTreeVisible: s.dataTreeVisible, inspectorVisible: s.inspectorVisible,
        treeSplitSizes: s.treeSplitSizes, inspectorSplitSizes: s.inspectorSplitSizes, isMaximized: s.isMaximized,
      }),
    }
  )
)
